import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity } from '../../utils/security';
import { toast } from 'react-toastify';
import { ArrowLeft, ExternalLink, Mail, Phone, Calendar, Clock, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminAuditDetail = () => {
    const { id } = useParams();
    const { user, isAdmin, loading: authLoading } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [audit, setAudit] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authLoading && !isAdmin()) navigate('/admin');
    }, [authLoading, isAdmin, navigate]);

    const fetchAudit = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchWithSecurity(`${API_URL}audits.php?id=${id}`);
            const data = await response.json();
            setAudit(data);
        } catch (error) {
            console.error('Error fetching audit:', error);
            toast.error(t('adminAuditDetail.toastLoadError') || 'აუდიტის დეტალების ჩატვირთვა ვერ მოხერხდა');
        }
        setLoading(false);
    }, [id, t]);

    useEffect(() => {
        if (user && id) fetchAudit();
    }, [user, id, fetchAudit]);

    const updateStatus = async (status) => {
        try {
            const response = await fetchWithSecurity(`${API_URL}audits.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: parseInt(id), status })
            });
            const result = await response.json();
            if (result.success) {
                toast.success(t('adminAuditDetail.toastStatusUpdated') || 'სტატუსი განახლდა');
                setAudit({ ...audit, status });
            } else {
                toast.error(result.error);
            }
        } catch (error) {
            toast.error(t('adminAuditDetail.toastStatusError') || 'სტატუსის განახლება ვერ მოხერხდა');
        }
    };

    if (authLoading || !user) return null;

    const getStatusBadge = (status) => {
        switch (status) {
            case 'New': return 'shadcn-badge-danger';
            case 'In Progress': return 'shadcn-badge-warning';
            case 'Completed': return 'shadcn-badge-success';
            case 'Archived': return 'shadcn-badge-secondary';
            default: return 'shadcn-badge-secondary';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'New': return t('adminAudit.statusNew') || 'ახალი';
            case 'In Progress': return t('adminAudit.statusInProgress') || 'მიმდინარე';
            case 'Completed': return t('adminAudit.statusCompleted') || 'დასრულებული';
            case 'Archived': return t('adminAudit.statusArchived') || 'დაარქივებული';
            default: return status;
        }
    };

    return (
        <AdminLayout title={t('adminAuditDetail.title') || 'ვებგვერდის აუდიტის მოთხოვნის დეტალები'}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <button className="shadcn-btn shadcn-btn-secondary" onClick={() => navigate('/admin/audits')}>
                    <ArrowLeft size={16} /> {t('adminAuditDetail.back') || 'აუდიტის სიასთან დაბრუნება'}
                </button>
                {audit && (
                    <span className={`shadcn-badge ${getStatusBadge(audit.status)}`}>
                        {getStatusLabel(audit.status)}
                    </span>
                )}
            </div>

            {loading ? (
                <div className="text-center py-5 text-muted">
                    <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                    <span>{t('adminAuditDetail.loading') || 'აუდიტის დეტალები იტვირთება...'}</span>
                </div>
            ) : audit ? (
                <div className="row g-4">
                    <div className="col-lg-8">
                        <div className="shadcn-card mb-4">
                            <div className="shadcn-card-header">
                                <div className="shadcn-card-title">
                                    <Search size={20} className="text-indigo" />
                                    <span>{t('adminAuditDetail.clientTechInfo') || 'კლიენტის და ტექნიკური ინფორმაცია'}</span>
                                </div>
                            </div>

                            <div className="d-flex flex-column gap-3">
                                <div className="p-3 rounded-3 bg-light border d-flex justify-content-between align-items-center">
                                    <span className="text-muted small">{t('adminAuditDetail.clientName') || 'კლიენტის სახელი'}</span>
                                    <span className="text-dark fw-bold">{audit.name}</span>
                                </div>
                                <div className="p-3 rounded-3 bg-light border d-flex justify-content-between align-items-center">
                                    <span className="text-muted small">{t('adminAuditDetail.email') || 'ელ. ფოსტა'}</span>
                                    <a href={`mailto:${audit.email}`} className="text-indigo text-decoration-none small fw-semibold">
                                        {audit.email}
                                    </a>
                                </div>
                                <div className="p-3 rounded-3 bg-light border d-flex justify-content-between align-items-center">
                                    <span className="text-muted small">{t('adminAuditDetail.phone') || 'ტელეფონის ნომერი'}</span>
                                    <a href={`tel:${audit.phone}`} className="text-indigo text-decoration-none small fw-semibold">
                                        {audit.phone || 'N/A'}
                                    </a>
                                </div>
                                <div className="p-3 rounded-3 bg-light border d-flex justify-content-between align-items-center">
                                    <span className="text-muted small">{t('adminAuditDetail.targetWebsite') || 'სამიზნე ვებგვერდი (URL)'}</span>
                                    <a href={audit.website_url || audit.website} target="_blank" rel="noreferrer" className="text-indigo text-decoration-none small fw-semibold d-flex align-items-center gap-1">
                                        {audit.website_url || audit.website} <ExternalLink size={13} />
                                    </a>
                                </div>
                            </div>
                        </div>

                        {audit.message && (
                            <div className="shadcn-card">
                                <div className="shadcn-card-header">
                                    <div className="shadcn-card-title">
                                        <span>{t('adminAuditDetail.notes') || 'კლიენტის შენიშვნები / შეტყობინება'}</span>
                                    </div>
                                </div>
                                <p className="text-dark mb-0 small" style={{ whiteSpace: 'pre-wrap' }}>{audit.message}</p>
                            </div>
                        )}
                    </div>

                    <div className="col-lg-4">
                        <div className="shadcn-card mb-4">
                            <div className="shadcn-card-header">
                                <div className="shadcn-card-title">
                                    <span>{t('adminAuditDetail.actions') || 'აუდიტის მოქმედებები'}</span>
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="form-group-label">{t('adminAuditDetail.statusLabel') || 'აუდიტის სტატუსი'}</label>
                                <select
                                    className={`shadcn-select ${getStatusBadge(audit.status)}`}
                                    value={audit.status}
                                    onChange={(e) => updateStatus(e.target.value)}
                                >
                                    <option value="New">{t('adminAudit.statusNew') || 'ახალი'}</option>
                                    <option value="In Progress">{t('adminAudit.statusInProgress') || 'მიმდინარე'}</option>
                                    <option value="Completed">{t('adminAudit.statusCompleted') || 'დასრულებული'}</option>
                                    <option value="Archived">{t('adminAudit.statusArchived') || 'დაარქივებული'}</option>
                                </select>
                            </div>

                            <div className="d-flex flex-column gap-2">
                                <a href={`mailto:${audit.email}?subject=Web Audit Report from OrbitroniX`} className="shadcn-btn shadcn-btn-primary w-100">
                                    <Mail size={16} /> {t('adminAuditDetail.sendEmail') || 'ანგარიშის გაგზავნა ელ.ფოსტით'}
                                </a>
                                {audit.phone && (
                                    <a href={`tel:${audit.phone}`} className="shadcn-btn shadcn-btn-secondary w-100">
                                        <Phone size={16} /> {t('adminAuditDetail.callClient') || 'კლიენტთან დარეკვა'}
                                    </a>
                                )}
                            </div>
                        </div>

                        <div className="shadcn-card">
                            <div className="shadcn-card-header">
                                <div className="shadcn-card-title">
                                    <span>{t('adminAuditDetail.metadata') || 'მეტამონაცემები'}</span>
                                </div>
                            </div>
                            <div className="d-flex justify-content-between mb-2 small">
                                <span className="text-muted d-flex align-items-center gap-1"><Calendar size={13} /> {t('adminAuditDetail.submitted') || 'გამოგზავნილია'}</span>
                                <span className="text-dark fw-semibold">{new Date(audit.created_at).toLocaleDateString()}</span>
                            </div>
                            <div className="d-flex justify-content-between small">
                                <span className="text-muted d-flex align-items-center gap-1"><Clock size={13} /> {t('adminAuditDetail.time') || 'დრო'}</span>
                                <span className="text-dark fw-semibold">{new Date(audit.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </AdminLayout>
    );
};

export default AdminAuditDetail;
